import React, { ChangeEvent, ReactNode, useState } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { cssTheme } from '../../../themes';
import { getLinariaClassName } from '../../../utils/getLinariaClassName';
import { baseInputSizeStyles, baseInputTypographyStyles, CaptionWrapper, CaratWrapper, EndIconsWrapper, getBaseInputIconPaddingStyles, InputBaseSize, InputWrapper, StartIconsWrapper, TextTransform, ValidationBadge } from '../helpers/InputBase';
import { Icon } from '../Icon';
import { Text } from '../Text';
interface Props {
  /** Field autocomplete id */
  autoComplete?: string;
  /** Message to display below input. Changes color along with validation highlights. */
  caption?: ReactNode;
  /** The options of the select element */
  children: ReactNode;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input has an error */
  hasError?: boolean;
  /** An Icon to display on the left side of the input for LTR languages. Inverted on RTL */
  icon?: JSX.Element;
  /** ID of this component, passed down by the controlling parent component */
  id?: string;
  /** Whether the input is rounded */
  isRounded?: boolean;
  /** Whether the user has interacted with this input */
  isTouched?: boolean;
  /** Name of this component, passed down by the controlling parent component */
  name?: string;
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
  /** An onChange callback passed down by the controlling parent component */
  onChange?: (value: string) => void;
  /** Callback that fires when the input is clicked */
  onClick?: () => void;
  /** Content to be used as the input placeholder */
  placeholder?: string;
  /** Formik function for setting a specific field's value. Used to update the value without sending an HTML event. */
  setFieldValue?: (field: string, value: unknown, shouldValidate?: boolean) => void;
  /** The size of the input */
  size?: InputBaseSize;
  /** CSS text-transform style to apply to the input field */
  textTransformType?: TextTransform;
  /** The value passed down by the controlling parent component */
  value?: string;
  /** Whether the input shows or not the validation icon */
  withValidationIcon?: boolean;
  /** Optional aria-label for screen-readers, used when no label is present */
  ariaLabel?: string;
}
const verticalPaddingStyles = "v13sze4t";
const _exp17 = /*#__PURE__*/() => ({
  value
}) => value === '' ? cssTheme.colors.input.default.placeholder : cssTheme.colors.input.default.text;
const _exp18 = /*#__PURE__*/() => ({
  sizeVariant,
  withStartIcon,
  isRounded
}) => getBaseInputIconPaddingStyles({
  sizeVariant,
  withStartIcon,
  isRounded
}).paddingLeft;
const _exp19 = /*#__PURE__*/() => ({
  sizeVariant,
  withStartIcon,
  isRounded
}) => getBaseInputIconPaddingStyles({
  sizeVariant,
  withStartIcon,
  isRounded
}).paddingRight;
const _exp20 = /*#__PURE__*/() => ({
  textTransformType
}) => textTransformType ? textTransformType : TextTransform.none;
export const InputSelectElement = /*#__PURE__*/styled('select')({
  name: "InputSelectElement",
  class: "i2g0jpm",
  propsAsIs: false,
  vars: {
    "i2g0jpm-0": [_exp17()],
    "i2g0jpm-1": [_exp18()],
    "i2g0jpm-2": [_exp19()],
    "i2g0jpm-3": [_exp20()]
  }
});
export const InputSelect = ({
  autoComplete,
  caption,
  children,
  dataId,
  disabled,
  hasError,
  icon,
  id,
  isRounded,
  isTouched,
  name,
  onBlur,
  onChange,
  onClick,
  placeholder,
  setFieldValue,
  size = 'lg',
  textTransformType = TextTransform.none,
  value,
  withValidationIcon = false,
  className,
  ariaLabel
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const showInvalid = isTouched && hasError;
  const showValid = isTouched && !hasError;
  const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    setIsOpen(false);
    if (onBlur) {
      onBlur(event);
    }
  };
  const handleChange = (event: ChangeEvent) => {
    const newValue = (event.currentTarget as HTMLSelectElement).value;
    setIsOpen(false);
    if (setFieldValue && name) {
      setFieldValue(name, newValue);
    } else if (onChange) {
      onChange(newValue);
    }
  };
  const handleClick = () => {
    if (onClick) onClick();
    setIsOpen(true);
  };
  return <InputWrapper className={className}>
      <InputSelectElement autoComplete={autoComplete} data-field-error={hasError} data-id={dataId} disabled={disabled} id={id} isRounded={isRounded} name={name} value={value} showInvalid={showInvalid} showValid={showValid} sizeVariant={size} textTransformType={textTransformType} withStartIcon={Boolean(icon)} onBlur={handleBlur} onChange={handleChange} onClick={handleClick} className={cx(`${getLinariaClassName(InputSelectElement)}--size-${size}`, `${baseInputSizeStyles}--size-${size}`, `${verticalPaddingStyles}--size-${size}`, `${baseInputTypographyStyles}--size-${size}`, isRounded && 'isRounded')} aria-label={ariaLabel}>
        {placeholder && <option disabled hidden value="">
            {placeholder}
          </option>}
        {children}
      </InputSelectElement>

      <StartIconsWrapper sizeVariant={size}>{icon}</StartIconsWrapper>
      <EndIconsWrapper isRounded={isRounded} sizeVariant={size}>
        {withValidationIcon ? <ValidationBadge showInvalid={showInvalid} showValid={showValid} /> : null}
        <CaratWrapper isOpen={isOpen} disabled={disabled} sizeVariant={size}>
          <Icon name="CaratUp" size="xs" />
        </CaratWrapper>
      </EndIconsWrapper>

      {caption && <CaptionWrapper showInvalid={showInvalid}>
          <Text variant="labelXS">{caption}</Text>
        </CaptionWrapper>}
    </InputWrapper>;
};

require("./InputSelect.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./InputSelect.tsx");